import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { fmtDate, formatIDR } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import PcmActionDialog from "./PcmActionDialog";
import LegacyQueue from "./LegacyQueue";
import LegacyResolveDialog from "./LegacyResolveDialog";

export const PCM_TONE = {
  pending_fat: "bg-amber-100 text-amber-800 border-amber-300",
  verified: "bg-emerald-100 text-emerald-800 border-emerald-300",
  source_unlinked: "bg-rose-50 text-rose-700 border-rose-300",
  draft: "bg-slate-100 text-slate-600 border-slate-300",
  rejected: "bg-rose-100 text-rose-800 border-rose-300",
  cancelled: "bg-slate-200 text-slate-500 border-slate-300 line-through",
};
const ALLOC = { belum: ["Belum Dialokasikan", "text-slate-500 border-slate-300"], sebagian: ["Sebagian Dialokasikan", "text-sky-700 border-sky-300 bg-sky-50"], penuh: ["Sudah Dialokasikan", "text-emerald-700 border-emerald-300 bg-emerald-50"] };

export function PcmBadge({ status, label, testid }) {
  return <span className={`inline-block px-1.5 py-px border text-[10px] font-semibold uppercase tracking-wider ${PCM_TONE[status] || PCM_TONE.draft}`} data-testid={testid}>{label}</span>;
}

function AllocBadge({ s, testid }) {
  if (!s) return null;
  const [l, c] = ALLOC[s];
  return <span className={`inline-block px-1.5 py-px border text-[10px] font-semibold uppercase tracking-wider ${c}`} data-testid={testid}>{l}</span>;
}

const FILTERS = [["", "Semua"], ["pending_fat", "Menunggu Verifikasi"], ["source_unlinked", "Sumber Belum Ditautkan"], ["verified", "Terverifikasi"], ["rejected", "Ditolak"], ["cancelled", "Dibatalkan"]];

export default function ManualPettyCashPanel({ onChanged }) {
  const [tab, setTab] = useState("realisasi");
  const [data, setData] = useState({ items: [], counts: {} });
  const [legacyOpen, setLegacyOpen] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [status, setStatus] = useState("");
  const [act, setAct] = useState(null);
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const canVerify = canPerm(user, "petty_cash", "verify_actual");
  const load = useCallback(async () => {
    try {
      const a = await api.get("/pc-manual-realizations");
      setData(a.data);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal memuat realisasi Catat Pengeluaran"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const done = () => { setAct(null); load(); setReloadKey((k) => k + 1); onChanged?.(); };
  const rows = data.items.filter((r) => !status || r.status === status);
  return (
    <section className="bg-white border border-slate-200" data-testid="pcm-panel">
      <header className="px-3 py-2 border-b border-slate-200 flex flex-wrap items-center gap-2 justify-between">
        <div className="inline-flex border border-slate-300">
          {[["realisasi", `Realisasi Petty Cash · Catat Pengeluaran (${data.items.length})`], ["legacy", `Pengeluaran Manual Perlu Ditautkan (${legacyOpen})`]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`pcm-tab-${k}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold ${tab === k ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <button type="button" onClick={() => { load(); setReloadKey((k) => k + 1); }} data-testid="pcm-refresh" className="p-1.5 border border-slate-300 hover:bg-slate-50" aria-label="Muat ulang"><RefreshCw size={12} /></button>
      </header>
      {tab === "realisasi" ? (
        <>
          <div className="px-3 py-2 flex flex-wrap gap-1" data-testid="pcm-status-filter">
            {FILTERS.map(([v, l]) => (
              <button key={v || "all"} type="button" onClick={() => setStatus(v)} data-testid={`pcm-filter-${v || "all"}`}
                className={`px-2 py-1 text-[10px] uppercase tracking-wider border ${status === v ? "border-[#0B1D3A] bg-[#0B1D3A] text-white" : "border-slate-300 text-slate-600"}`}>
                {l}{v && data.counts[v] ? ` (${data.counts[v]})` : ""}
              </button>
            ))}
          </div>
          <RealisasiTable rows={rows} canVerify={canVerify} onAct={setAct} />
        </>
      ) : null}
      <div className={tab === "legacy" ? "" : "hidden"}>
        <LegacyQueue canVerify={canVerify} onAct={setAct} reloadKey={reloadKey} onTotal={setLegacyOpen} />
      </div>
      {act && ["legacy", "reopen"].includes(act.kind) && <LegacyResolveDialog act={act} onClose={() => setAct(null)} onDone={done} />}
      {act && !["legacy", "reopen"].includes(act.kind) && <PcmActionDialog act={act} onClose={() => setAct(null)} onDone={done} />}
    </section>
  );
}

function RealisasiTable({ rows, canVerify, onAct }) {
  if (!rows.length) return <p className="px-3 py-6 text-center text-xs italic text-slate-400" data-testid="pcm-empty">Belum ada realisasi pada filter ini.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="pcm-table">
        <thead className="bg-slate-50 border-y border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
          <tr className="text-left">
            <th className="px-2 py-1.5">Tgl Transaksi</th><th className="px-2 py-1.5">Tgl Lapor</th><th className="px-2 py-1.5">Uraian</th>
            <th className="px-2 py-1.5">Proyek / WO</th><th className="px-2 py-1.5">Sumber Petty Cash</th>
            <th className="px-2 py-1.5 text-right">Nominal</th><th className="px-2 py-1.5">Status</th><th className="px-2 py-1.5 text-right">Aksi</th>
          </tr>
        </thead>
        <tbody>{rows.map((r) => <RealisasiRow key={r.id} r={r} canVerify={canVerify} onAct={onAct} />)}</tbody>
      </table>
    </div>
  );
}

function RealisasiRow({ r, canVerify, onAct }) {
  const btn = (kind, label, tone = "border-slate-300 text-slate-700") => (
    <button type="button" onClick={() => onAct({ kind, r })} data-testid={`pcm-${kind}-${r.id}`}
      className={`px-1.5 py-0.5 border text-[10px] uppercase tracking-wider font-semibold hover:bg-slate-50 ${tone}`}>{label}</button>
  );
  return (
    <tr className="border-b border-slate-100 align-top" data-testid={`pcm-row-${r.id}`}>
      <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(r.transaction_date)}</td>
      <td className="px-2 py-1.5 whitespace-nowrap text-slate-500">{fmtDate(String(r.reported_at || "").slice(0, 10))}</td>
      <td className="px-2 py-1.5">
        <div className="text-slate-800">{r.description}</div>
        <div className="text-[10px] text-slate-500">{r.category}{r.reference_no ? ` · ${r.reference_no}` : ""}{r.proof_url ? <> · <a href={r.proof_url} target="_blank" rel="noreferrer" className="underline">Bukti</a></> : null}</div>
        <div className="text-[9px] font-semibold uppercase tracking-wider text-orange-700 mt-0.5">{r.origin_label}</div>
        {r.possible_duplicates?.length > 0 && (
          <div className="text-[10px] text-amber-700 inline-flex items-center gap-1 mt-0.5" data-testid={`pcm-dup-${r.id}`}>
            <AlertTriangle size={10} /> Mirip: {r.possible_duplicates.map((d) => `${d.label} (${d.reasons.join(", ")})`).join("; ")}
          </div>
        )}
      </td>
      <td className="px-2 py-1.5">
        <Link to={`/projects/${r.project_id}/cost-control`} className="text-[#0B1D3A] underline decoration-dotted" data-testid={`pcm-open-cc-${r.id}`}>{r.project_name || "-"}</Link>
        {r.wo_number && <div className="text-[10px] text-slate-500">Saran: {String(r.wo_number).replace(/^MAIN:.*/, "Main")}</div>}
      </td>
      <td className="px-2 py-1.5">
        <div>{r.holder_name || <span className="italic text-rose-600">Pemegang belum dipilih</span>}</div>
        {r.pc_id ? <Link to={`/petty-cash?open=${r.pc_id}`} className="text-[10px] underline decoration-dotted text-slate-500" data-testid={`pcm-open-pc-${r.id}`}>{r.pc_week_label || "Pengajuan"}</Link>
          : <div className="text-[10px] text-rose-600">Pengajuan belum ditautkan</div>}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums">
        <div className="font-semibold">{formatIDR(r.amount)}</div>
        {r.status === "verified" && <div className="text-[10px] text-slate-500">Teralokasi {formatIDR(r.allocated_total)} · Sisa {formatIDR(r.remaining)}</div>}
      </td>
      <td className="px-2 py-1.5 space-y-0.5">
        <PcmBadge status={r.status} label={r.status_label} testid={`pcm-status-${r.id}`} />
        <div><AllocBadge s={r.allocation_status} testid={`pcm-alloc-${r.id}`} /></div>
        {r.reject_reason && <div className="text-[10px] text-rose-700">Alasan: {r.reject_reason}</div>}
        {r.cancel_reason && <div className="text-[10px] text-slate-500">Alasan: {r.cancel_reason}</div>}
      </td>
      <td className="px-2 py-1.5 text-right">
        <div className="inline-flex flex-wrap justify-end gap-1">
          {r.status === "source_unlinked" && btn("link", "Tautkan Sumber")}
          {r.status === "pending_fat" && canVerify && btn("verify", "Verifikasi", "border-emerald-400 text-emerald-700")}
          {r.status === "pending_fat" && canVerify && btn("reject", "Tolak", "border-rose-300 text-rose-700")}
          {["pending_fat", "source_unlinked", "verified"].includes(r.status) && btn("correct", "Koreksi")}
          {r.status !== "cancelled" && btn("cancel", "Batalkan")}
          {btn("history", "Riwayat")}
        </div>
      </td>
    </tr>
  );
}
